import React, { type ChangeEvent, useCallback, useEffect, useState } from 'react';
import { styled } from '@compiled/react';
import shuffle from 'lodash/shuffle';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { ErrorMessage, Field, HelperMessage } from '@atlaskit/form';
import { SpotlightPulse } from '@atlaskit/onboarding';
import { Box, xcss } from '@atlaskit/primitives';
import Select from '@atlaskit/select';
import Textfield from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';
import { type IntlShape, useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import {
	defaultDepartmentOptionsWithEmoji,
	OTHER_DEPARTMENT,
	PROJECT_TEAM_TYPE_FREE_TEXT_MAX_LENGTH,
} from './constants';
import messages from './messages';
import { ProjectDepartmentTypeFreeTextLabel } from './project-department-type-free-text';
import type { SelectOption } from './types';

type InnerProps = {
	value?: string;
	freeTextValue?: string;
	onChange: (value: SelectOption | null) => void;
	onFreeTextChange: (value: string | undefined, error: boolean) => void;
	templateKey?: string;
	validateRequired: boolean;
};

export const toSelectOption = (
	teamType: string | undefined,
	formatMessage: IntlShape['formatMessage'],
): SelectOption | undefined => {
	// map teamType to the list of pre-defined departments
	const departmentOpts = defaultDepartmentOptionsWithEmoji
		.filter((department) => department.id === teamType)
		.map((department) => ({
			// @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ placeholder: MessageDescriptorV2; label: MessageDescriptorV2; other: MessageDescriptorV2; software: MessageDescriptorV2; ... 10 more ...; marketing: MessageDescriptorV2; }'.
			label: formatMessage(messages[department.id]),
			value: department.id,
			id: department.id,
			isUGC: department.isUGC,
			icon: department.icon,
		}))
		.pop();
	if (departmentOpts) {
		return departmentOpts;
	}

	// if team type is Other, return the Other department option
	if (teamType === OTHER_DEPARTMENT) {
		return {
			label: formatMessage(messages.other),
			value: OTHER_DEPARTMENT,
			id: OTHER_DEPARTMENT,
			isUGC: true,
		};
	}
	// fallback to undefined to display placeholder text in the dropdown
	return undefined;
};

export const DepartmentTypeSelect = (props: InnerProps) => {
	const { formatMessage } = useIntl();
	const { value, freeTextValue, onChange, validateRequired, onFreeTextChange } = props;

	const [options, setOptions] = useState<SelectOption[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [showFreeText, setShowFreeText] = useState(false);
	const [formattedValue, setFormattedValue] = useState<SelectOption | undefined>();
	const [showFreeTextErrorMessage, setShowFreeTextErrorMessage] = useState(false);
	const [hasValidationError, setHasValidationError] = useState<boolean>(false);

	const getOptionValue = (option: SelectOption) => option.label;
	const formatOptionLabel = (option: SelectOption) => (
		<>
			{option.icon ? <IconWrapper>{option.icon}</IconWrapper> : null}
			{option.label}
		</>
	);

	const validateFreeTextField = (input: string) => {
		const isTooLong = input.length > PROJECT_TEAM_TYPE_FREE_TEXT_MAX_LENGTH;
		setShowFreeTextErrorMessage(isTooLong);
		return isTooLong;
	};
	useEffect(() => {
		setOptions([
			// 'value' is the data that is sent in analytics and stored in the backend and can be mutable.
			// 'id' is static and is used to determine the type of department category selected.
			...shuffle(defaultDepartmentOptionsWithEmoji).map((department) => ({
				// @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ placeholder: MessageDescriptorV2; label: MessageDescriptorV2; other: MessageDescriptorV2; software: MessageDescriptorV2; ... 10 more ...; marketing: MessageDescriptorV2; }'.
				label: formatMessage(messages[department.id]),
				value: department.id,
				id: department.id,
				icon: department.icon,
				isUGC: department.isUGC,
			})),
			{
				label: formatMessage(messages.other),
				value: OTHER_DEPARTMENT,
				id: OTHER_DEPARTMENT,
				isUGC: false,
			},
		]);
		const selectedOption = toSelectOption(value, formatMessage);
		freeTextValue && validateFreeTextField(freeTextValue);
		setFormattedValue(selectedOption);
		setShowFreeText(selectedOption?.value === OTHER_DEPARTMENT);
		setIsLoading(false);
	}, [formatMessage, value, freeTextValue]);

	const handleChange = useCallback(
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		(newValue: SelectOption | null, _: any, analyticsEvent?: UIAnalyticsEvent) => {
			onChange(newValue);
			newValue && setHasValidationError(false);

			const isOtherSelected = newValue?.value === OTHER_DEPARTMENT;
			setShowFreeText(isOtherSelected);

			// reset the free text value when switching from other to a different pre-defined option
			if (value === OTHER_DEPARTMENT && !isOtherSelected) {
				setShowFreeTextErrorMessage(false);
				onFreeTextChange(undefined, false);
			}
			// @ts-expect-error - Argument of type 'UIAnalyticsEvent | undefined' is not assignable to parameter of type 'UIAnalyticsEvent'.
			fireUIAnalytics(analyticsEvent, 'departmentFieldDropdown', {
				...(newValue && { optionValue: newValue.value }),
			});
		},
		[onChange, onFreeTextChange, value],
	);

	const handleFreeTextChange = useCallback(
		(event: ChangeEvent<HTMLInputElement>) => {
			const isNotValid = validateFreeTextField(event.target.value);
			onFreeTextChange(event.target.value, isNotValid);
		},
		[onFreeTextChange],
	);

	const handleBlur = () => {
		if (!validateRequired) return;
		value ? setHasValidationError(false) : setHasValidationError(true);
	};

	return (
		<Box xcss={labelWrapperStyles}>
			<Field
				isRequired={validateRequired}
				name="department-type"
				label={formatMessage(messages.label)}
			>
				{({ fieldProps: { id, ...rest } }) => (
					<>
						<SpotlightPulse pulse={!value} radius={2}>
							<Select
								inputId={id}
								{...rest}
								classNamePrefix="project-create.create-form.department-type-select"
								isDisabled={isLoading}
								isLoading={isLoading}
								formatOptionLabel={formatOptionLabel}
								getOptionValue={getOptionValue}
								options={options}
								value={formattedValue}
								onChange={handleChange}
								placeholder={formatMessage(messages.placeholder)}
								required={validateRequired}
								onBlur={handleBlur}
							/>
						</SpotlightPulse>
						<HelperMessage>{formatMessage(messages.footer)}</HelperMessage>
						{hasValidationError && (
							<ErrorMessage>{formatMessage(messages.departmentFieldRequired)}</ErrorMessage>
						)}
					</>
				)}
			</Field>
			{showFreeText && (
				<Box xcss={freeTextFieldWrapperStyles}>
					<Field
						isRequired={false}
						name="department-type-other"
						label={<ProjectDepartmentTypeFreeTextLabel />}
					>
						{({ fieldProps }) => (
							<>
								<Textfield
									{...fieldProps}
									isDisabled={isLoading}
									onChange={handleFreeTextChange}
									value={
										formattedValue?.id === OTHER_DEPARTMENT && formattedValue.isUGC
											? freeTextValue
											: undefined
									}
								/>
								{showFreeTextErrorMessage && (
									<ErrorMessage>
										{formatMessage(messages.tooLongProjectTeamTypeError, {
											maxCharacters: PROJECT_TEAM_TYPE_FREE_TEXT_MAX_LENGTH,
										})}
									</ErrorMessage>
								)}
							</>
						)}
					</Field>
				</Box>
			)}
		</Box>
	);
};

export default DepartmentTypeSelect;

const labelWrapperStyles = xcss({
	marginTop: 'space.200',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IconWrapper = styled.span({
	marginRight: token('space.050', '4px'),
});

const freeTextFieldWrapperStyles = xcss({
	marginTop: 'space.200',
});
